import { makeAutoObservable, runInAction, toJS } from 'mobx';
import axios from 'axios';
import { uploadFiles } from 'global/utils/uploadFiles';
import { UploadUrls } from 'types/carrierFileService';
import { processRfqTemplateFile } from 'api/operations/productServiceV2';
import { getRootStoreInstance } from 'store/rootStore';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import ModalDelegate from 'store/general/modalDelegate';
import FileUploadDelegate from 'store/general/fileUploadDelegate';

export class UploadJsonModalDelegate {
  private _rootStore = getRootStoreInstance();
  private _modalDelegate = new ModalDelegate();
  private _productId = '';
  private _productName = '';

  private _isModalLoading = false;

  public fileUploadDelegate = new FileUploadDelegate();

  public get isVisible(): boolean {
    return this._modalDelegate.isVisible;
  }

  public get isModalLoading(): boolean {
    return this._isModalLoading;
  }

  public get isFileUploadReset(): boolean {
    return this.fileUploadDelegate.isFileUploadReset;
  }

  public get productName(): string {
    return this._productName;
  }

  private setIsModalLoading(value: boolean): void {
    this._isModalLoading = value;
  }

  public open(productId: string, productName: string): void {
    this._productId = productId;
    this._productName = productName;
    this.fileUploadDelegate.setIsFileUploadReset(false);
    this._modalDelegate.open();
  }

  public close(): void {
    this._modalDelegate.close();
    this.fileUploadDelegate.resetFiles();
    this.fileUploadDelegate.setIsFileUploadReset(true);
    this._productId = '';
    this._productName = '';
  }

  public async uploadFile(): Promise<void> {
    const uploadedFileName = this.fileUploadDelegate.uploadedFileNames[0];
    const productId = this._productId;
    const sponsorId = this._rootStore.domainStoreV2.sponsorId;
    const files = toJS(this.fileUploadDelegate.uploadedFiles);

    if (!uploadedFileName || !productId) {
      return;
    }

    this.setIsModalLoading(true);

    try {
      const res = await axios<UploadUrls>({
        url: `https://${process.env.REACT_APP_API_BASE_URL}/productV2/Prod/uploadProduct?fileName=${uploadedFileName}`,
        withCredentials: true,
      });

      const fileUrlData: Record<string, string> = { [uploadedFileName]: Object.values(res.data)[0] };

      await uploadFiles(files, fileUrlData);
      await processRfqTemplateFile(productId, uploadedFileName, sponsorId);

      runInAction(() => {
        this.setIsModalLoading(false);
        this.fileUploadDelegate.setIsFileUploadReset(true);
      });
    } catch (error) {
      runInAction(() => {
        toastManager.addToast('An issue occurred during the file upload process.', ToastColorTypes.WARNING);
        this.setIsModalLoading(false);
        this.close();
      });
    }
  }

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
